import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { cn, formatDate, formatNumber } from '@/lib/utils';
import { apiService as mockApi } from '@/api';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Drawer } from '@/components/ui/Drawer';
import { LoadingState, EmptyState } from '@/components/ui/States';
import { toast } from '@/components/ui/Toast';
import type { Site, Client, Device } from '@/types';
import {
  Building2,
  Plus,
  Search,
  Filter,
  X,
  MapPin,
  Globe,
  Clock,
  Monitor,
  ChevronRight,
  Cpu,
  HardDrive,
  Users,
  ExternalLink,
  Shield,
  Activity,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';

const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'America/Toronto',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Paris',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Asia/Dubai',
  'Australia/Sydney',
  'UTC',
];

export function Sites() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [sites, setSites] = useState<Site[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [clientFilter, setClientFilter] = useState<string>(searchParams.get('client') || 'all');
  const [countryFilter, setCountryFilter] = useState<string>('all');

  // Add Site Modal State
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newSite, setNewSite] = useState({
    name: '',
    clientId: '',
    address: '',
    city: '',
    country: 'USA',
    timezone: 'America/New_York',
  });

  // Site Drawer / Details
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [siteDevices, setSiteDevices] = useState<Device[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [loadedSites, loadedClients] = await Promise.all([
          mockApi.getSites(),
          mockApi.getClients(),
        ]);
        setSites(loadedSites);
        setClients(loadedClients);

        // Pre-select client filter from URL if present
        const urlClient = searchParams.get('client');
        if (urlClient) {
          setClientFilter(urlClient);
        }
      } catch (err) {
        toast('Failed to load sites', 'error');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [searchParams]);

  // Derived list of unique countries for dropdown
  const uniqueCountries = useMemo(() => {
    const set = new Set(sites.map((s) => s.country).filter(Boolean));
    return Array.from(set).sort();
  }, [sites]);

  // Map of client ID -> Client Name
  const clientMap = useMemo(() => {
    const map = new Map<string, string>();
    clients.forEach((c) => map.set(c.id, c.name));
    return map;
  }, [clients]);

  // Filtered Sites
  const filteredSites = useMemo(() => {
    return sites.filter((site) => {
      const clientName = clientMap.get(site.clientId) || '';
      const matchesSearch =
        site.name.toLowerCase().includes(search.toLowerCase()) ||
        site.city.toLowerCase().includes(search.toLowerCase()) ||
        site.address.toLowerCase().includes(search.toLowerCase()) ||
        clientName.toLowerCase().includes(search.toLowerCase());

      const matchesClient = clientFilter === 'all' || site.clientId === clientFilter;
      const matchesCountry = countryFilter === 'all' || site.country === countryFilter;

      return matchesSearch && matchesClient && matchesCountry;
    });
  }, [sites, search, clientFilter, countryFilter, clientMap]);

  // Summary Metrics
  const metrics = useMemo(() => {
    const totalSites = sites.length;
    const totalCountries = uniqueCountries.length;
    const totalDevices = sites.reduce((sum, s) => sum + (s.deviceCount || 0), 0);
    const uniqueClients = new Set(sites.map((s) => s.clientId)).size;

    return { totalSites, totalCountries, totalDevices, uniqueClients };
  }, [sites, uniqueCountries]);

  // Load site devices when drawer opens
  const handleSelectSite = async (site: Site) => {
    setSelectedSite(site);
    setLoadingDevices(true);
    try {
      const resp = await mockApi.getDevices({ site: site.name, pageSize: 100 });
      setSiteDevices(resp.data);
    } catch {
      toast('Failed to fetch devices for this site', 'error');
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleAddSite = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSite.name.trim()) {
      toast('Site name is required', 'error');
      return;
    }
    if (!newSite.clientId) {
      toast('Please select a client', 'error');
      return;
    }

    setSaving(true);
    setTimeout(() => {
      const created: Site = {
        id: `site-${Date.now()}`,
        clientId: newSite.clientId,
        tenantId: 'tenant-1',
        name: newSite.name,
        address: newSite.address || '100 Tech Way',
        city: newSite.city || 'San Francisco',
        country: newSite.country || 'USA',
        lat: 37.7749,
        lng: -122.4194,
        deviceCount: 0,
        timezone: newSite.timezone,
        createdAt: new Date().toISOString(),
      };

      setSites([created, ...sites]);
      setSaving(false);
      setAddModalOpen(false);
      setNewSite({
        name: '',
        clientId: '',
        address: '',
        city: '',
        country: 'USA',
        timezone: 'America/New_York',
      });
      toast(`Site "${created.name}" created successfully`);
    }, 400);
  };

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-text-primary tracking-tight">Sites</h1>
            <Badge variant="default" className="text-xs font-mono">
              {sites.length} {sites.length === 1 ? 'Site' : 'Sites'}
            </Badge>
          </div>
          <p className="text-xs text-text-muted mt-1">
            Physical and logical locations across clients, managing regional devices and timezone policies.
          </p>
        </div>

        <Button
          variant="primary"
          onClick={() => {
            if (clients.length > 0 && !newSite.clientId) {
              setNewSite((prev) => ({ ...prev, clientId: clients[0].id }));
            }
            setAddModalOpen(true);
          }}
          className="gap-2 shrink-0"
        >
          <Plus className="h-4 w-4" />
          Add Site
        </Button>
      </div>

      {/* Metrics Summary Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="surface p-3.5 rounded-lg flex items-center justify-between">
          <div>
            <span className="text-2xs font-medium text-text-muted uppercase tracking-wider">Total Sites</span>
            <div className="text-xl font-bold tabular mt-1 text-text-primary">{formatNumber(metrics.totalSites)}</div>
          </div>
          <div className="h-9 w-9 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center text-accent">
            <Building2 className="h-4 w-4" />
          </div>
        </div>

        <div className="surface p-3.5 rounded-lg flex items-center justify-between">
          <div>
            <span className="text-2xs font-medium text-text-muted uppercase tracking-wider">Clients Represented</span>
            <div className="text-xl font-bold tabular mt-1 text-text-primary">{formatNumber(metrics.uniqueClients)}</div>
          </div>
          <div className="h-9 w-9 rounded-lg bg-base-elevated border border-border flex items-center justify-center text-text-secondary">
            <Users className="h-4 w-4" />
          </div>
        </div>

        <div className="surface p-3.5 rounded-lg flex items-center justify-between">
          <div>
            <span className="text-2xs font-medium text-text-muted uppercase tracking-wider">Total Devices</span>
            <div className="text-xl font-bold tabular mt-1 text-status-online">{formatNumber(metrics.totalDevices)}</div>
          </div>
          <div className="h-9 w-9 rounded-lg bg-status-online/10 border border-status-online/20 flex items-center justify-center text-status-online">
            <Monitor className="h-4 w-4" />
          </div>
        </div>

        <div className="surface p-3.5 rounded-lg flex items-center justify-between">
          <div>
            <span className="text-2xs font-medium text-text-muted uppercase tracking-wider">Countries / Regions</span>
            <div className="text-xl font-bold tabular mt-1 text-text-primary">{formatNumber(metrics.totalCountries)}</div>
          </div>
          <div className="h-9 w-9 rounded-lg bg-base-elevated border border-border flex items-center justify-center text-text-secondary">
            <Globe className="h-4 w-4" />
          </div>
        </div>
      </div>

      {/* Filter and Control Bar */}
      <div className="surface p-3 rounded-lg flex flex-col md:flex-row items-center justify-between gap-3">
        <div className="flex flex-1 flex-wrap items-center gap-3 w-full">
          {/* Search Box */}
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-muted" />
            <input
              type="text"
              placeholder="Search site, city, client or address..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-base pl-9 text-xs"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Client Filter Dropdown */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-text-muted font-medium whitespace-nowrap">Client:</span>
            <select
              value={clientFilter}
              onChange={(e) => {
                setClientFilter(e.target.value);
                if (e.target.value === 'all') {
                  searchParams.delete('client');
                } else {
                  searchParams.set('client', e.target.value);
                }
                setSearchParams(searchParams);
              }}
              className="input-base text-xs py-1.5 w-44"
            >
              <option value="all">All Clients ({clients.length})</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Country Filter Dropdown */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-text-muted font-medium whitespace-nowrap">Country:</span>
            <select
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              className="input-base text-xs py-1.5 w-40"
            >
              <option value="all">All Countries</option>
              {uniqueCountries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </div>
        </div>

        {(search || clientFilter !== 'all' || countryFilter !== 'all') && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearch('');
              setClientFilter('all');
              setCountryFilter('all');
              searchParams.delete('client');
              setSearchParams(searchParams);
            }}
            className="text-xs text-text-muted hover:text-text-primary shrink-0"
          >
            Clear Filters
          </Button>
        )}
      </div>

      {/* Main Table / Content */}
      {loading ? (
        <LoadingState rows={8} />
      ) : filteredSites.length === 0 ? (
        <EmptyState
          icon={<Building2 className="h-10 w-10 stroke-[1.5]" />}
          title="No sites found"
          description={
            search || clientFilter !== 'all' || countryFilter !== 'all'
              ? 'No sites match your active filter criteria.'
              : 'No sites have been registered yet.'
          }
          action={
            search || clientFilter !== 'all' || countryFilter !== 'all' ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setSearch('');
                  setClientFilter('all');
                  setCountryFilter('all');
                  searchParams.delete('client');
                  setSearchParams(searchParams);
                }}
              >
                Reset Filters
              </Button>
            ) : (
              <Button variant="primary" size="sm" onClick={() => setAddModalOpen(true)}>
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add First Site
              </Button>
            )
          }
        />
      ) : (
        <div className="surface overflow-hidden rounded-lg">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-base-elevated border-b border-border text-text-muted font-medium uppercase tracking-wider">
                <tr>
                  <th className="py-3 px-4">Site Name</th>
                  <th className="py-3 px-4">Client</th>
                  <th className="py-3 px-4">Location</th>
                  <th className="py-3 px-4">Timezone</th>
                  <th className="py-3 px-4 text-center">Devices</th>
                  <th className="py-3 px-4">Created Date</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredSites.map((site) => {
                  const clientName = clientMap.get(site.clientId) || 'Unknown Client';

                  return (
                    <tr
                      key={site.id}
                      className="hover:bg-base-hover/50 transition-colors cursor-pointer group"
                      onClick={() => handleSelectSite(site)}
                    >
                      {/* Site Name & Icon */}
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2.5">
                          <div className="h-7 w-7 rounded bg-accent/10 border border-accent/20 flex items-center justify-center text-accent shrink-0">
                            <Building2 className="h-3.5 w-3.5" />
                          </div>
                          <div>
                            <div className="font-semibold text-text-primary group-hover:text-accent transition-colors flex items-center gap-1.5">
                              {site.name}
                            </div>
                            <div className="text-2xs text-text-muted truncate max-w-[200px]">{site.address}</div>
                          </div>
                        </div>
                      </td>

                      {/* Client */}
                      <td className="py-3 px-4">
                        <span className="font-medium text-text-secondary">{clientName}</span>
                      </td>

                      {/* Location */}
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1.5 text-text-secondary">
                          <MapPin className="h-3 w-3 text-text-muted shrink-0" />
                          <span>
                            {site.city}, <strong className="text-text-primary font-normal">{site.country}</strong>
                          </span>
                        </div>
                      </td>

                      {/* Timezone */}
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1 text-2xs font-mono text-text-muted bg-base-elevated px-2 py-0.5 rounded border border-border">
                          <Clock className="h-3 w-3" />
                          {site.timezone}
                        </span>
                      </td>

                      {/* Devices Badge */}
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center gap-1 font-mono font-bold text-xs text-status-online bg-status-online/10 px-2.5 py-0.5 rounded-full border border-status-online/20">
                          <Monitor className="h-3 w-3" />
                          {site.deviceCount || 0}
                        </span>
                      </td>

                      {/* Created */}
                      <td className="py-3 px-4 text-text-muted">
                        {formatDate(site.createdAt, 'MMM d, yyyy')}
                      </td>

                      {/* Actions */}
                      <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSelectSite(site)}
                          className="h-7 px-2 text-xs text-text-secondary hover:text-text-primary gap-1"
                        >
                          <span>Details</span>
                          <ChevronRight className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Site Modal */}
      <Modal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        title="Add New Site"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setAddModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" loading={saving} onClick={handleAddSite}>
              Create Site
            </Button>
          </>
        }
      >
        <form onSubmit={handleAddSite} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">
              Client <span className="text-status-critical">*</span>
            </label>
            <select
              required
              value={newSite.clientId}
              onChange={(e) => setNewSite({ ...newSite, clientId: e.target.value })}
              className="input-base"
            >
              <option value="" disabled>
                Select a client...
              </option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">
              Site Name <span className="text-status-critical">*</span>
            </label>
            <input
              type="text"
              required
              placeholder="e.g. Headquarters, West Coast DC"
              value={newSite.name}
              onChange={(e) => setNewSite({ ...newSite, name: e.target.value })}
              className="input-base"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Street Address</label>
            <input
              type="text"
              placeholder="e.g. 500 Market St, Suite 400"
              value={newSite.address}
              onChange={(e) => setNewSite({ ...newSite, address: e.target.value })}
              className="input-base"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">City</label>
              <input
                type="text"
                placeholder="e.g. San Francisco"
                value={newSite.city}
                onChange={(e) => setNewSite({ ...newSite, city: e.target.value })}
                className="input-base"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Country</label>
              <input
                type="text"
                placeholder="e.g. USA"
                value={newSite.country}
                onChange={(e) => setNewSite({ ...newSite, country: e.target.value })}
                className="input-base"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Timezone</label>
            <select
              value={newSite.timezone}
              onChange={(e) => setNewSite({ ...newSite, timezone: e.target.value })}
              className="input-base"
            >
              {COMMON_TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </div>
        </form>
      </Modal>

      {/* Site Details Drawer */}
      <Drawer
        open={Boolean(selectedSite)}
        onClose={() => setSelectedSite(null)}
        title={selectedSite ? `Site: ${selectedSite.name}` : ''}
        width={560}
      >
        {selectedSite && (
          <div className="p-5 space-y-6">
            {/* Top Overview Banner */}
            <div className="surface p-4 rounded-lg space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-base font-bold text-text-primary">{selectedSite.name}</h3>
                  <p className="text-xs text-accent font-medium mt-0.5">
                    {clientMap.get(selectedSite.clientId) || 'Client'}
                  </p>
                </div>
                <Badge variant="online" dot>
                  Active Site
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs pt-2 border-t border-border">
                <div className="flex items-center gap-1.5 text-text-muted">
                  <MapPin className="h-3.5 w-3.5 text-text-secondary shrink-0" />
                  <span className="truncate">{selectedSite.address}, {selectedSite.city}</span>
                </div>
                <div className="flex items-center gap-1.5 text-text-muted">
                  <Globe className="h-3.5 w-3.5 text-text-secondary shrink-0" />
                  <span>{selectedSite.country}</span>
                </div>
                <div className="flex items-center gap-1.5 text-text-muted">
                  <Clock className="h-3.5 w-3.5 text-text-secondary shrink-0" />
                  <span className="font-mono text-2xs">{selectedSite.timezone}</span>
                </div>
                <div className="flex items-center gap-1.5 text-text-muted">
                  <Shield className="h-3.5 w-3.5 text-text-secondary shrink-0" />
                  <span>GPS: {selectedSite.lat.toFixed(2)}, {selectedSite.lng.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Devices Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-semibold text-text-primary uppercase tracking-wider flex items-center gap-2">
                  <Monitor className="h-3.5 w-3.5 text-accent" />
                  Deployed Devices ({siteDevices.length})
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/devices?site=${selectedSite.name}`)}
                  className="text-2xs text-accent hover:text-accent-hover h-6 px-1.5"
                >
                  <span>Open Devices Page</span>
                  <ExternalLink className="h-3 w-3 ml-1" />
                </Button>
              </div>

              {loadingDevices ? (
                <LoadingState rows={4} />
              ) : siteDevices.length === 0 ? (
                <div className="surface p-6 text-center text-xs text-text-muted rounded-lg">
                  No registered devices found at this site.
                </div>
              ) : (
                <div className="space-y-2">
                  {siteDevices.map((device) => (
                    <div
                      key={device.id}
                      onClick={() => navigate(`/devices/${device.id}`)}
                      className="surface p-3 rounded-lg flex items-center justify-between gap-3 hover:border-border-strong transition-all cursor-pointer group"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-text-primary text-xs group-hover:text-accent transition-colors">
                            {device.hostname}
                          </span>
                          <Badge variant={device.status} className="text-2xs">
                            {device.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 text-2xs text-text-muted mt-1">
                          <span>{device.os}</span>
                          <span>•</span>
                          <span className="font-mono">{device.publicIp || device.localIp}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 text-2xs tabular text-right shrink-0">
                        <div>
                          <div className="text-text-muted flex items-center gap-1">
                            <Cpu className="h-3 w-3 text-text-muted" />
                            <span>CPU</span>
                          </div>
                          <div className="font-semibold text-text-primary mt-0.5">{device.cpu}%</div>
                        </div>

                        <div>
                          <div className="text-text-muted flex items-center gap-1">
                            <HardDrive className="h-3 w-3 text-text-muted" />
                            <span>RAM</span>
                          </div>
                          <div className="font-semibold text-text-primary mt-0.5">{device.memory}%</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
